import React, {useEffect, useId, useRef} from 'react';
import {useSelector} from 'react-redux';

import {StickyPageLayout} from '@/components/Layout';
import PageHeaderMedia from '@/components/PageHeaderMedia/PageHeaderMedia';
import PopupResponsive from '@/components/Popup/PopupResponsive';
import useOneModalOnPageAtATime from '@/components/Popup/useOneModalOnPageAtATime';
import appConfig from '@/config/appConfig.typed';
import ModalTypes from '@/consts/app/modalTypes';
import useIsMobileOnlyLayout from '@/hooks/layout/useIsMobileOnlyLayout';
import amplitudeNotificationTracking from '@/services/analytics/amplitude/amplitudeNotificationTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import log from '@/services/logger/log';
import pushNotificationService from '@/services/pushNotification/pushNotificationService';
import notificationStorageService from '@/services/storage/notificationStorageService';
import {selectPushNotificationIsAnyOperationInProgress} from '@/state/selectors/pushNotification';
import {hideModalAction} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import EnableNotificationModalButton from './EnableNotificationModalButton';
import EnableNotificationModalContent from './EnableNotificationModalContent';

const EnableNotificationModal = () => {
    const isAnyOperationInProgress = useSelector(selectPushNotificationIsAnyOperationInProgress);
    const popupRef = useRef(null);
    const headerId = useId();

    const isMobileOnly = useIsMobileOnlyLayout();
    const imageMedia = appConfig.getEnableNotificationPageHeaderImageMedia();
    const videoSrc = appConfig.getEnableNotificationPageHeaderVideoSrc();
    const videoThumbnailSrc = appConfig.getEnableNotificationPageHeaderVideoThumbnailSrc();

    useOneModalOnPageAtATime();

    const onClick = async () => {
        try {
            notificationStorageService.setIsNotificationPromptClosed(true);
            amplitudeNotificationTracking.trackEnableNotificationScreenNextButton();
            googleAnalyticsService.trackEnableNotificationsNextButtonClick();
            await pushNotificationService.requestSubscription();
        } catch (error) {
            log.info(`EnableNotificationModal: Error requesting push notification subscription: ${error}`);
        }

        dispatch(hideModalAction(ModalTypes.ENABLE_NOTIFICATION));
    };

    const onClose = () => {
        notificationStorageService.setIsNotificationPromptClosed(true);
        dispatch(hideModalAction(ModalTypes.ENABLE_NOTIFICATION));
    };

    useEffect(() => {
        amplitudeNotificationTracking.trackEnableNotificationScreen();
        googleAnalyticsService.trackEnableNotificationsScreenView();
    }, []);

    return (
        <PopupResponsive
            shouldShowFullPage={isMobileOnly}
            className='flex rounded-t-xl md:left-1/2 md:-translate-x-1/2 md:rounded-3xl md:text-center ml:max-w-[458px] ml:p-8'
            overlayRef={popupRef}
            onClose={onClose}
            shouldCloseOnOverlayClick={false}
            aria-labelledby={headerId}
        >
            {isMobileOnly && (
                <WarningBannersTop
                    source={WARNING_BANNER_SOURCE_TYPES.ENABLE_NOTIFICATION_POPUP}
                    containerRef={popupRef}
                />
            )}
            {isMobileOnly ? (
                <StickyPageLayout
                    className='items-center pt-safe'
                    contentClassName='flex flex-1 flex-col items-center'
                    footer={
                        <EnableNotificationModalButton
                            disabled={isAnyOperationInProgress}
                            loading={isAnyOperationInProgress}
                            onClick={onClick}
                        />
                    }
                >
                    <PageHeaderMedia
                        isVideoAvailable={!!videoThumbnailSrc}
                        videoThumbnailSrc={videoThumbnailSrc}
                        videoSrc={videoSrc}
                        imageMedia={imageMedia}
                    />
                    <EnableNotificationModalContent headerId={headerId} onBtnClick={onClick} />
                    <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.ENABLE_NOTIFICATION_POPUP} />
                </StickyPageLayout>
            ) : (
                <EnableNotificationModalContent headerId={headerId}>
                    <EnableNotificationModalButton
                        disabled={isAnyOperationInProgress}
                        loading={isAnyOperationInProgress}
                        headerId={headerId}
                        onClick={onClick}
                    />
                </EnableNotificationModalContent>
            )}
        </PopupResponsive>
    );
};

export default EnableNotificationModal;
